import { Block, EmptyCircle, Eyebrow, Row, SageCheck } from '../primitives';
import { AlignmentMeters } from '../alignment-meters';
import type { DailyGuidance } from '../../lib/api';
import { instantDay, ordinal, shortDay } from '../../lib/format';

/** SC-28 simple view. `heading` renders the headline block itself (the desktop Align hub has no page header for it). */
export function GuidancePanel({ g, heading }: { g: DailyGuidance; heading?: boolean }) {
  const from = g.reference === 'ascendant' ? 'ascendant' : 'Moon sign';
  return (
    <>
      {heading && (
        <section className="border-b border-border px-5 py-6 lg:py-8">
          <Eyebrow className="mb-2.5">Today's Vedic guidance · {shortDay(g.day)}</Eyebrow>
          <h2 className="font-display text-[1.75rem] font-semibold leading-[1.15] text-ink xl:text-[2rem]">
            {g.headline}
          </h2>
          <p className="mt-2.5 text-[0.95rem] leading-relaxed text-ink/70">{g.summary}</p>
        </section>
      )}
      <section className="grid gap-6 border-b border-border px-5 py-6 sm:grid-cols-2 lg:py-7">
        <div>
          <Eyebrow className="mb-3">Best for</Eyebrow>
          <ul className="space-y-2.5">
            {g.bestFor.map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-[0.95rem] text-ink">
                <SageCheck size={18} /> {item}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <Eyebrow className="mb-3">Use more care with</Eyebrow>
          <ul className="space-y-2.5">
            {g.careWith.map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-[0.95rem] text-ink">
                <EmptyCircle size={18} /> {item}
              </li>
            ))}
          </ul>
        </div>
      </section>
      <section className="space-y-5 border-b border-border px-5 py-6 lg:py-7">
        <Eyebrow>Today's alignment</Eyebrow>
        <AlignmentMeters alignment={g.alignment} />
      </section>
      <Row
        title={`Moon in ${g.moon.sign}, ${g.moon.nakshatra}`}
        meta={`${ordinal(g.moon.natalHouse)} house from your ${from}`}
      />
      {g.dasha && (
        <Row
          title={`${g.dasha.maha} Mahadasha, ${g.dasha.antar} Antardasha`}
          meta={`Pratyantar ${g.dasha.pratyantar} · sub-period until ${instantDay(g.dasha.antarEnds)}`}
        />
      )}
      {g.backdrop.map((b) => (
        <Block key={b.planet} eyebrow={`${b.planet} · ${ordinal(b.natalHouse)} house`}>
          {b.planet} {b.note}
        </Block>
      ))}
      <details className="border-b border-border px-5 py-5">
        <summary className="eyebrow cursor-pointer hover:text-ink">All planets today</summary>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="eyebrow">
                <th className="py-2 font-medium">Planet</th>
                <th className="py-2 font-medium">Sign</th>
                <th className="py-2 font-medium">House</th>
              </tr>
            </thead>
            <tbody>
              {g.transits.map((t) => (
                <tr key={t.planet} className="border-t border-border text-ink">
                  <td className="py-2">
                    {t.planet}
                    {t.retrograde && t.planet !== 'Rahu' && t.planet !== 'Ketu' ? ' (R)' : ''}
                  </td>
                  <td className="py-2">{t.sign}</td>
                  <td className="py-2 font-mono">{t.natalHouse}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
      <p className="px-5 py-6 text-xs leading-relaxed text-muted-foreground">{g.disclaimer}</p>
    </>
  );
}
